import { Alert, Stack } from '@mantine/core';
import { ContextModalProps } from '@mantine/modals';
import { z } from 'zod';

import { CompanyPartner, CompanyProfile } from 'models';
import { CompanyHelpers } from 'helpers/CompanyHelpers';

import { Form } from 'components/forms/Form';
import { FormSubmit } from 'components/forms/FormSubmit';
import { ImageField } from 'components/forms/fields/ImageField';
import { TextField } from 'components/forms/fields/TextField';
import { URLField } from 'components/forms/fields/URLField';
import { useCompanyProfile } from 'hooks/use-company-profile';
import { CompanyProfileService } from 'services/CompanyProfileService';
import { LocalNotificationService } from 'services/LocalNotificationService';
import { ModalService } from 'services/ModalService';

const FormValidator = z.object({
    name: z.string().min(3),
    description: z.string().optional(),
    linkedin: z.string().url().optional(),
    email: z.string().toLowerCase().email().optional(),
    logo: z.string(),
});

const CreatePartnerModal = ({
    innerProps,
}: ContextModalProps<{
    company: CompanyProfile;
}>) => {
    const { company: incomingCompany } = innerProps;

    const { company } = useCompanyProfile(incomingCompany);

    const handleAdd = async (partner: CompanyPartner & { email?: string }) => {
        await CompanyProfileService.update(incomingCompany.id, {
            partners: [partner, ...(company?.partners ?? [])],
        });

        LocalNotificationService.showSuccess({
            message: 'Partner added',
        });

        ModalService.closeModal(CompanyHelpers.modals.createPartner);
    };

    return (
        <Form<CompanyPartner> onSubmit={handleAdd} zodSchema={FormValidator}>
            <Stack>
                <TextField name="name" label="Partner Name" />

                <TextField
                    name="description"
                    label="Description"
                    placeholder="How do you collaborate with this partner?"
                />

                <URLField name="linkedin" label="LinkedIn" />

                <ImageField
                    name="logo"
                    label="Logo"
                    buttonLabel="Upload a logo"
                    group={`company:${incomingCompany.id}:images`}
                />

                <TextField name="email" label="Contact Email (optional)" placeholder="info@partner.com" />

                <Alert fz="xs" color="primary" p="xs">
                    Help expand our database by adding a contact email. We will contact the partner to add their company
                    profile to our database.
                </Alert>

                <FormSubmit>Add partner</FormSubmit>
            </Stack>
        </Form>
    );
};

export { CreatePartnerModal };
